import { Navigate, Outlet, Route } from 'react-router-dom';

import { ResponsiveLayout } from '@/components/widgets/t/ResponsiveLayout/ResponsiveLayout.layout';
import { BuyerCompleteAccountPage } from '@/pages/buyer/pages/complete-account/complete-account.page';
import { RestrictedAccessPage } from '@/pages/restricted-access/RestrictedAccess.page';
import { AccountPageWrapper } from '@/pages/shared/account/components/Account.page-wrapper';
import { AccountEditEmailPage } from '@/pages/shared/account/pages/edit-email/EditEmail.page';
import { AccountEditNamePage } from '@/pages/shared/account/pages/edit-name/EditName.page';
import { AccountEditPasswordPage } from '@/pages/shared/account/pages/edit-password/EditPassword.page';
import { AccountPage } from '@/pages/shared/account/pages/index/Account.page';
import { AccountLegalPage } from '@/pages/shared/account/pages/legal/AccountLegal.page';
import { AccountTeamPage } from '@/pages/shared/account/pages/team/AccountTeam.page';
import { AccountTeamEditMemberPage } from '@/pages/shared/account/pages/team/pages/edit-member/AccountTeamEditMember.page';
import { AccountTeamInvitePage } from '@/pages/shared/account/pages/team/pages/invite/AccountTeamInvite.page';
import { ComparePage } from '@/pages/shared/compare/Compare.page';
import { CompareListPage } from '@/pages/shared/compare/CompareList.page';
import { ComplianceFrameworksPage } from '@/pages/shared/compliance-frameworks/ComplianceFrameworks.page';
import { CopilotPage } from '@/pages/shared/copilot/Copilot.page';
import { DisclosurePage } from '@/pages/shared/disclosure/Disclosure.page';
import { HomePage } from '@/pages/shared/home/Home.page';
import { IndicatorsPage } from '@/pages/shared/indicators/Indicators.page';
import { KnowledgePage } from '@/pages/shared/knowledge/Knowledge.page';
import { LandBiodiversityPage } from '@/pages/shared/land/LandBiodiversity.page';
import { LandCarbonPage } from '@/pages/shared/land/LandCarbon.page';
import { LandSocialPage } from '@/pages/shared/land/LandSocial.page';
import { LandSoilPage } from '@/pages/shared/land/LandSoil.page';
import { LandWaterPage } from '@/pages/shared/land/LandWater.page';
import { MediaUploadPage } from '@/pages/shared/media-upload/MediaUpload.page';
import { PlotOverviewPage } from '@/pages/shared/media-upload/pages/PlotOverview.page';
import { OrganizationOnboardingPage } from '@/pages/shared/organization/pages/Onboarding.page';
import { InteractiveMapPage } from '@/pages/shared/plots/pages/InteractiveMap.page';
import { PlotGroupsPage } from '@/pages/shared/plots/PlotGroups.page';
import { PlotsPage } from '@/pages/shared/plots/Plots.page';
import { ContractsPage } from '@/pages/shared/portfolio/pages/contracts/Contracts.page';
import { ForecastsPage } from '@/pages/shared/portfolio/pages/forecasts/Forecasts.page';
import { PortfolioNaturalCapitalPage } from '@/pages/shared/portfolio/pages/NaturalCapital/PortfolioNaturalCapital.page';
import { ScenarioForecastsPage } from '@/pages/shared/portfolio/pages/scenarios/ScenarioForecasts.page';
import { StocksPage } from '@/pages/shared/portfolio/pages/stocks/Stocks.page';
import { UpliftUnitsPage } from '@/pages/shared/portfolio/pages/uplift/UpliftUnits.page';
import { PortfolioInvestmentsPage } from '@/pages/shared/portfolio/PortfolioInvestments.page';
import { DetailsPage } from '@/pages/shared/projects/pages/project/Details.page';
import { NDVIDetailPage } from '@/pages/shared/projects/pages/project/pages/conservation/restoration/pages/ndvi/NDVIDetail.page';
import { WaterDetailPage as WaterDetailConservationPage } from '@/pages/shared/projects/pages/project/pages/conservation/restoration/pages/water/WaterDetail.page';
import { InteractiveMap } from '@/pages/shared/projects/pages/project/pages/conservation/shared/pages/interactive-map/interactive-map.page';
import { PlotPage } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/Plot.page';
import { ProjectPage } from '@/pages/shared/projects/pages/project/Project.page';
import { ProjectsPage } from '@/pages/shared/projects/Projects.page';
import { ReportsPage } from '@/pages/shared/reports/Reports.page';
import { InSituDataPage } from '@/pages/shared/uploads/pages/InSituData.page';
import { MediaMapsPage } from '@/pages/shared/uploads/pages/MediaMaps.page';
import { MediaMapsConfigurationPage } from '@/pages/shared/uploads/pages/pages/MediaMapsConfiguration.page';
import { UploadsPage } from '@/pages/shared/uploads/Uploads.page';
import { WorkstreamsPage } from '@/pages/shared/workstreams/Workstreams.page';
import { paths } from '@/routing';
import {
  EcologicalIntegrityPages,
  HabitatIntactnessPages,
  IndicatorSpeciesPages,
} from '@/routing/components/ConservationPages';
import { HomePageProtection } from '@/routing/components/protections/home-page/HomePageProtection';
import { PlotGroupDetailsCleaner } from '@/routing/components/protections/PlotGroupDetailsCleaner/PlotGroupDetailsCleaner';
import { RequireAccessiblePlot } from '@/routing/components/protections/require-accessible-plot/RequireAccessiblePlot';
import { RequireAccessibleProject } from '@/routing/components/protections/require-accessible-project/RequireAccessibleProject';
import { RequireAdminRole } from '@/routing/components/protections/require-admin-role/RequireAdminRole';
import { NoGroupByProtection } from '@/routing/components/protections/require-group-by/NoGroupByProtection';
import { RequireSupportPlot } from '@/routing/components/protections/require-support-plot/RequireSupportPlot';
import { UploadsProtection } from '@/routing/components/protections/require-uploads/UploadsProtection';
import { SkipLoggedInUser } from '@/routing/components/protections/skip-logged-in-user/SkipLoggedInUser';

import { ComplianceFrameworksPageWrapper } from '../../shared/compliance-frameworks/components/ComplianceFrameworks.page-wrapper';
import { BuyerInvestmentsPage } from '../pages/investments/Investments.page';
import { BuyerPortfolioPage } from '../pages/portfolio/Portfolio.page';
import { BuyerSignedInRoute } from './components/protections/buyer-signed-in-route/BuyerSignedInRoute';

export const BuyerPageRoutes = (
  <Route path='buyer'>
    <Route element={<BuyerSignedInRoute />}>
      <Route element={<ResponsiveLayout />}>
        <Route element={<PlotGroupDetailsCleaner />}>
          <Route path={paths.buyer.account} element={<AccountPageWrapper />}>
            <Route index element={<AccountPage />} />
            <Route path={paths.buyer.accountEditEmail} element={<AccountEditEmailPage />} />
            <Route path={paths.buyer.accountEditName} element={<AccountEditNamePage />} />
            <Route path={paths.buyer.accountEditPassword} element={<AccountEditPasswordPage />} />
            <Route path={paths.buyer.accountLegal} element={<AccountLegalPage />} />
            <Route path={paths.buyer.accountTeam}>
              <Route index element={<AccountTeamPage />} />
              <Route element={<RequireAdminRole />}>
                <Route path={paths.buyer.accountTeamEditMember} element={<AccountTeamEditMemberPage />} />
                <Route path={paths.buyer.accountTeamInvite} element={<AccountTeamInvitePage />} />
              </Route>
            </Route>
          </Route>

          <Route path={paths.buyer.socialDetails} element={<LandSocialPage />} />

          <Route path={paths.buyer.portfolio} element={<BuyerPortfolioPage />} />
          <Route path={paths.buyer.portfolioNaturalCapital} element={<PortfolioNaturalCapitalPage />} />
          <Route path={paths.buyer.portfolioForecasts} element={<ForecastsPage />} />
          <Route path={paths.buyer.portfolioInvestments} element={<PortfolioInvestmentsPage />} />
          <Route path={paths.buyer.portfolioUnits} element={<UpliftUnitsPage />} />
          <Route path={paths.buyer.portfolioStocks} element={<StocksPage />} />
          <Route path={paths.buyer.portfolioContracts} element={<ContractsPage />} />

          <Route path={`${paths.buyer.scenarioModels}/*`} element={<ScenarioForecastsPage />} />

          <Route path={`${paths.buyer.disclosure}/*`} element={<DisclosurePage />} />

          <Route index element={<Navigate to={paths.buyer.projects} />} />
          <Route path={paths.buyer.investments} element={<BuyerInvestmentsPage />} />
          <Route element={<RequireSupportPlot />}>
            <Route path={paths.buyer.plots} element={<PlotsPage />} />
            <Route element={<NoGroupByProtection />}>
              <Route path={paths.buyer.groups} element={<PlotGroupsPage />} />
              <Route path={paths.buyer.comparisons} element={<CompareListPage />} />
              <Route path={paths.buyer.compare} element={<ComparePage />} />
            </Route>
          </Route>

          <Route element={<NoGroupByProtection />}>
            <Route path={`${paths.buyer.details}/*`} element={<DetailsPage />} />
            <Route path={paths.buyer.biodiversityDetails} element={<LandBiodiversityPage />} />
            <Route path={paths.buyer.soilDetails} element={<LandSoilPage />} />
            <Route path={paths.buyer.carbonDetails} element={<LandCarbonPage />} />
            <Route path={paths.buyer.waterDetails} element={<LandWaterPage />} />
          </Route>

          <Route path={paths.buyer.projects}>
            <Route index element={<ProjectsPage />} />
            <Route path={paths.buyer.projectsComplianceFrameworks} element={<ComplianceFrameworksPageWrapper />}>
              <Route index element={<ComplianceFrameworksPage />} />
            </Route>
            <Route element={<RequireAccessibleProject />}>
              <Route path={paths.buyer.ecologicalIntegrity} element={<EcologicalIntegrityPages />} />
              <Route path={paths.buyer.habitatIntactness} element={<HabitatIntactnessPages />} />
              <Route path={paths.buyer.indicatorSpecies} element={<IndicatorSpeciesPages />} />
              <Route path={paths.buyer.ndvi} element={<NDVIDetailPage />} />
              <Route path={`${paths.buyer.projectDetails}/*`} element={<ProjectPage />} />
              <Route path={paths.buyer.interactiveMap} element={<InteractiveMap />} />
              <Route path={paths.buyer.projectDetailsWater} element={<WaterDetailConservationPage />} />
            </Route>
          </Route>
          <Route element={<RequireAccessiblePlot />}>
            <Route path={`${paths.buyer.plot}/*`} element={<PlotPage />} />
            <Route path={paths.buyer.plotInteractiveMap} element={<InteractiveMapPage />} />
          </Route>

          <Route path={paths.buyer.restrictedAccess} element={<RestrictedAccessPage />} />
          <Route path={paths.buyer.indicators} element={<IndicatorsPage />} />
          <Route element={<HomePageProtection />}>
            <Route path={paths.buyer.home} element={<HomePage />} />
          </Route>
          <Route path={paths.buyer.contracts} element={<ContractsPage />} />
          <Route path={paths.buyer.reports} element={<ReportsPage />} />

          <Route path={paths.buyer.copilot} element={<CopilotPage />} />
          <Route path={paths.buyer.knowledge} element={<KnowledgePage />} />
          <Route path={paths.buyer.workstreams} element={<WorkstreamsPage />} />
          <Route element={<UploadsProtection />}>
            <Route path={paths.buyer.uploads} element={<UploadsPage />} />
            <Route path={paths.buyer.uploadsPlotOverview} element={<MediaUploadPage />} />
            <Route path={paths.buyer.uploadsPlotOverviewPlot} element={<PlotOverviewPage />} />
            <Route path={paths.buyer.insituDataUpload} element={<InSituDataPage />} />
            <Route path={paths.buyer.mediaMaps} element={<MediaMapsPage />} />
            <Route path={paths.buyer.mediaMapsConfiguration} element={<MediaMapsConfigurationPage />} />
          </Route>
        </Route>
        <Route path={paths.buyer.organizationOnboarding} element={<OrganizationOnboardingPage />} />
      </Route>
    </Route>
    <Route
      element={
        <SkipLoggedInUser>
          <Outlet />
        </SkipLoggedInUser>
      }
    >
      <Route path={paths.buyer.completeAccount} element={<BuyerCompleteAccountPage />} />
    </Route>
  </Route>
);
